import { useMemo } from 'react';
import { useAppSelector } from './useAppSelector';

// Constantes de roles
export const ROLES = {
  ADMIN: 'admin',
  USER: 'user',
};

// Hook para manejar roles y permisos
export const useRoles = () => {
  const { adminData } = useAppSelector(state => state.adminAuth);

  // Obtener el rol del usuario actual
  const userRole = useMemo(() => {
    return adminData?.role || null;
  }, [adminData]);

  // Verificar si el usuario tiene un rol específico
  const hasRole = useMemo(() => {
    return (role) => {
      return userRole === role;
    };
  }, [userRole]);

  // Verificar si el usuario tiene alguno de los roles especificados
  const hasAnyRole = useMemo(() => {
    return (roles) => {
      if (!Array.isArray(roles)) {
        roles = [roles];
      }
      return roles.includes(userRole);
    };
  }, [userRole]);

  // Verificar si el usuario es administrador
  const isAdmin = useMemo(() => {
    return userRole === ROLES.ADMIN;
  }, [userRole]);

  // Verificar si el usuario es usuario normal
  const isUser = useMemo(() => {
    return userRole === ROLES.USER;
  }, [userRole]);

  // Verificar si el usuario tiene permisos para una acción específica
  const can = useMemo(() => {
    return (action) => {
      const permissions = {
        // Permisos de administrador
        'manage-events': [ROLES.ADMIN],
        'manage-users': [ROLES.ADMIN],
        'manage-students': [ROLES.ADMIN],
        'manage-reservations': [ROLES.ADMIN],
        'view-dashboard': [ROLES.ADMIN, ROLES.USER],
        'view-reports': [ROLES.ADMIN],
        'manage-system': [ROLES.ADMIN],
        
        // Permisos de usuario
        'view-own-reservations': [ROLES.ADMIN, ROLES.USER],
        'create-reservations': [ROLES.ADMIN, ROLES.USER],
      };

      const allowedRoles = permissions[action] || [];
      return allowedRoles.includes(userRole);
    };
  }, [userRole]);

  return {
    userRole,
    hasRole,
    hasAnyRole,
    isAdmin,
    isUser,
    can,
    ROLES,
  };
}; 